import mixpanel from 'mixpanel-browser';

let isInitialized = false;
let currentUser = null;

/**
 * Initialize Mixpanel with token from environment
 */
export function init() {
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;

  if (!token) {
    console.warn('MIXPANEL_TOKEN not found in environment. Mixpanel tracking disabled.');
    return;
  }

  try {
    mixpanel.init(token, {
      debug: process.env.NODE_ENV === 'development', // Enable debug in development
      track_pageview: false, // We'll track manually
      persistence: 'localStorage',
    });
    isInitialized = true;
    console.log('Mixpanel initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Mixpanel:', error);
  }
}

/**
 * Identify user by email and create/update user profile
 * @param {string} email - User email (distinct identifier)
 * @param {object} properties - Optional initial user properties
 */
export function identify(email, properties = {}) {
  if (!isInitialized) return;

  if (!email) {
    console.warn('Mixpanel: Cannot identify user without email');
    return;
  }

  try {
    // Set currentUser FIRST before calling mixpanel.identify
    // This prevents race condition where trackEvent might be called before identify completes
    currentUser = email;

    // Identify the user
    mixpanel.identify(email);

    // Always set $name and $email for user profile
    const userProfile = {
      $email: email,
      $name: properties.name || email,
      ...properties
    };

    // Create or update user profile using set_once (doesn't overwrite existing values)
    // and set (updates current values)
    mixpanel.people.set_once({
      $email: email,
      $created: new Date().toISOString(),
      signup_method: properties.signup_method || 'unknown',
    });

    // Update current user properties
    mixpanel.people.set(userProfile);

    console.log('Mixpanel: User identified and profile updated:', email);
  } catch (error) {
    console.error('Mixpanel: Failed to identify user:', error);
  }
}

/**
 * Track an event with properties
 * @param {string} eventName - Human-readable event name (e.g., "Message Sent")
 * @param {object} properties - Event properties (use snake_case)
 */
export function trackEvent(eventName, properties = {}) {
  if (!isInitialized) return;

  if (!currentUser) {
    console.warn('Mixpanel: Cannot track event before user is identified. Call identify() first.');
    return;
  }

  try {
    const eventData = {
      ...properties,
      timestamp: Date.now(),
      environment: process.env.NODE_ENV || 'development',
    };

    mixpanel.track(eventName, eventData);
  } catch (error) {
    console.error(`Mixpanel: Failed to track event "${eventName}":`, error);
  }
}

/**
 * Set or update user properties
 * @param {object} properties - User properties to set (use snake_case)
 */
export function setUserProperties(properties = {}) {
  if (!isInitialized) return;

  if (!currentUser) {
    console.warn('Mixpanel: User not identified, cannot set properties');
    return;
  }

  try {
    mixpanel.people.set(properties);
  } catch (error) {
    console.error('Mixpanel: Failed to set user properties:', error);
  }
}

/**
 * Increment a user property by a value
 * @param {string} property - Property name
 * @param {number} value - Value to increment by (default 1)
 */
export function incrementUserProperty(property, value = 1) {
  if (!isInitialized) return;

  if (!currentUser) {
    console.warn('Mixpanel: User not identified, cannot increment property');
    return;
  }

  try {
    mixpanel.people.increment(property, value);
  } catch (error) {
    console.error(`Mixpanel: Failed to increment property "${property}":`, error);
  }
}

/**
 * Build user properties object from user data
 * @param {object} user - User object
 * @param {object} userStats - User stats object (optional)
 * @returns {object} User properties for Mixpanel
 */
export function buildUserProperties(user, userStats = null) {
  if (!user) return {};

  const properties = {
    // Demographic properties
    email: user.email,
    signup_date: user.createdAt || new Date().toISOString(),
    signup_method: 'thinkific_sso',
  };

  // Add user profile data
  if (user.financial_experience) properties.financial_experience = user.financial_experience;
  if (user.money_stage) properties.money_stage = user.money_stage;
  if (user.monthly_savings) properties.monthly_savings = user.monthly_savings;
  if (user.age_range) properties.age_range = user.age_range;
  if (user.countries) properties.countries = user.countries;
  if (user.thinkific_id) properties.thinkific_id = user.thinkific_id;
  if (user.currentMission) properties.current_mission = user.currentMission;

  // Add user stats if provided
  if (userStats) {
    if (userStats.journey_start_date) properties.journey_start_date = userStats.journey_start_date;
    if (userStats.days_active !== undefined) properties.days_active = userStats.days_active;
    if (userStats.total_sessions !== undefined) properties.total_sessions = userStats.total_sessions;
    if (userStats.last_active_date) properties.last_active_date = userStats.last_active_date;
    if (userStats.engagement_trend) properties.engagement_trend = userStats.engagement_trend;
    if (userStats.todos_completed !== undefined) properties.todos_completed = userStats.todos_completed;
    if (userStats.articles_read !== undefined) properties.articles_read = userStats.articles_read;
    if (userStats.videos_watched !== undefined) properties.videos_watched = userStats.videos_watched;

    // Calculate journey stage
    if (userStats.days_active !== undefined) {
      properties.journey_stage = getJourneyStage(userStats.days_active);
    }

    // Calculate days since signup
    if (userStats.journey_start_date) {
      properties.days_since_signup = getDaysSinceSignup(userStats.journey_start_date);
    }
  }

  return properties;
}

/**
 * Calculate journey stage from days active
 * @param {number} daysActive
 * @returns {string} Journey stage
 */
export function getJourneyStage(daysActive) {
  if (daysActive <= 3) return 'new';
  if (daysActive <= 7) return 'early';
  if (daysActive <= 13) return 'regular';
  return 'established';
}

/**
 * Calculate days since signup
 * @param {string|Date} journeyStartDate
 * @returns {number} Days since signup
 */
export function getDaysSinceSignup(journeyStartDate) {
  if (!journeyStartDate) return 0;
  const signupDate = new Date(journeyStartDate);
  const today = new Date();
  return Math.floor((today - signupDate) / (1000 * 60 * 60 * 24));
}

/**
 * Reset Mixpanel tracking (on logout)
 */
export function reset() {
  if (!isInitialized) return;

  try {
    mixpanel.reset();
    currentUser = null;
  } catch (error) {
    console.error('Mixpanel: Failed to reset:', error);
  }
}

// Initialize on import
init();
